import first from "lodash/first";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseImage } from "~/components/common/base-image";
import { withHighlightedHeadline } from "~/components/content/content-item-render/hoc";
import { useContentItemProviders } from "~/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import type { homeMultimediaPodcastContentItemContent$key } from "~/queries/__generated__/homeMultimediaPodcastContentItemContent.graphql";

import { Container, Content, Headline, Label, StyledEntityLink, StyledFigure } from "./styles";

export type Props = {
  reference: homeMultimediaPodcastContentItemContent$key;
} & ContentItemRenderVariantProps;

const Component = forwardRef<HTMLDivElement, Props>(
  ({ className, onClick, reference: reference_ }, reference) => {
    const content = useFragment(
      graphql`
        fragment homeMultimediaPodcastContentItemContent on Content {
          entityId
          ... on Article {
            images {
              style(filter: { style: "100x100" }) {
                url
              }
            }
          }
          ... on Podcast {
            podcastSections {
              images {
                style(filter: { style: "100x100" }) {
                  url
                }
              }
              name
            }
          }
          ...entityLink
          ...hooksContentItemProvidersContent @arguments(withHeadline: true)
        }
      `,
      reference_,
    );
    const providers = useContentItemProviders(content);

    const firstPodcastSection = first(content?.podcastSections);
    const sectionName = firstPodcastSection?.name ?? "";
    const podcastImageUrl = first(firstPodcastSection?.images)?.style?.url ?? "";
    const articleImageUrl = first(content?.images)?.style?.url ?? "";
    const imageUrl = podcastImageUrl || articleImageUrl;

    return (
      <Container className={className} ref={reference}>
        <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
          <Content>
            <Label>PODCAST</Label>
            <Headline>
              {providers.headline({ hideTooltip: true, preferSocialHeadline: true })}
            </Headline>
          </Content>
          <StyledFigure $aspectRatio="1/1">
            <BaseImage
              $aspectRatio="1/1"
              $objectFit="cover"
              alt={sectionName}
              src={imageUrl}
              lazyload
            />
          </StyledFigure>
        </StyledEntityLink>
      </Container>
    );
  },
);

Component.displayName = "ContentItemHomeMultimediaPodcast";

export const ContentItemHomeMultimediaPodcast = withHighlightedHeadline(Component);
